import { useEffect, useMemo, useRef, useState } from "react";
import "./SearchMobHeaderContent.scss";
import { IoArrowBack, IoCloseCircle, IoMic, IoSearchOutline, IoTimeOutline } from "react-icons/io5";

import { SEARCH_SUGGESTIONS } from "../../Products/data/phoneCaseFilters";

/** Hardcoded until search API is wired */
const RECENT_SEARCHES = SEARCH_SUGGESTIONS;

const TRENDING = [
    { label: "iPhone 15", query: "iPhone 15 cases" },
    { label: "MagSafe", query: "iPhone 14 MagSafe" },
    { label: "Galaxy S24", query: "Galaxy S24 case" },
    { label: "Clear", query: "Clear case" },
    { label: "Spigen", query: "Spigen" },
    { label: "OtterBox", query: "OtterBox" },
];

function useVoiceSearch(onTranscript) {
    const [listening, setListening] = useState(false);
    const [supported, setSupported] = useState(false);
    const recognitionRef = useRef(null);
    const onTranscriptRef = useRef(onTranscript);
    onTranscriptRef.current = onTranscript;

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) return undefined;

        setSupported(true);
        const recognition = new SpeechRecognition();
        recognition.lang = "en-IN";
        recognition.interimResults = true;
        recognition.continuous = false;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => setListening(true);
        recognition.onend = () => setListening(false);
        recognition.onerror = () => setListening(false);
        recognition.onresult = (event) => {
            const transcript = Array.from(event.results)
                .map((r) => r?.[0]?.transcript || "")
                .join(" ")
                .trim();
            onTranscriptRef.current(transcript, event.results[event.resultIndex]?.isFinal);
        };

        recognitionRef.current = recognition;

        return () => {
            recognition.onstart = null;
            recognition.onend = null;
            recognition.onerror = null;
            recognition.onresult = null;
            recognition.stop();
            recognitionRef.current = null;
        };
    }, []);

    const toggle = () => {
        if (!recognitionRef.current) return;
        if (listening) {
            recognitionRef.current.stop();
            return;
        }
        try {
            recognitionRef.current.start();
        } catch {
            setListening(false);
        }
    };

    return { listening, supported, toggle };
}

export function SearchMobHeaderContent({ setSelectedHeader }) {
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);

    const trimmed = query.trim();
    const isEmpty = trimmed.length === 0;

    const results = useMemo(() => {
        if (isEmpty) return RECENT_SEARCHES;
        const q = trimmed.toLowerCase();
        return RECENT_SEARCHES.filter((item) => item.toLowerCase().includes(q));
    }, [isEmpty, trimmed]);

    const handleTranscript = (text, isFinal) => {
        if (text) setQuery(text);
        if (isFinal) inputRef.current?.focus();
    };

    const { listening, supported, toggle: toggleMic } = useVoiceSearch(handleTranscript);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        const t = window.setTimeout(() => inputRef.current?.focus(), 50);
        return () => {
            window.clearTimeout(t);
            document.body.style.overflow = "";
        };
    }, []);

    const pick = (value) => {
        setQuery(value);
        inputRef.current?.focus();
    };

    const close = () => setSelectedHeader(2);

    return (
        <div className="mob-search" role="dialog" aria-modal="true" aria-label="Search">
            <header className="mob-search__bar">
                <button type="button" className="mob-search__back" onClick={close} aria-label="Go back">
                    <IoArrowBack aria-hidden />
                </button>

                <div className="mob-search__field">
                    <IoSearchOutline className="mob-search__field-icon" aria-hidden />
                    <input
                        ref={inputRef}
                        className="mob-search__input"
                        type="search"
                        inputMode="search"
                        enterKeyHint="search"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="Search for brands & products"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {query ? (
                        <button
                            type="button"
                            className="mob-search__clear"
                            onClick={() => pick("")}
                            aria-label="Clear search"
                        >
                            <IoCloseCircle aria-hidden />
                        </button>
                    ) : null}
                    <span className="mob-search__field-sep" aria-hidden />
                    <button
                        type="button"
                        className={`mob-search__mic${listening ? " mob-search__mic--active" : ""}`}
                        onClick={toggleMic}
                        disabled={!supported}
                        aria-label={listening ? "Stop voice search" : "Voice search"}
                    >
                        <IoMic aria-hidden />
                    </button>
                </div>
            </header>

            <div className="mob-search__scroll">
                {isEmpty ? (
                    <section className="mob-search__block">
                        <h2 className="mob-search__heading">Trending</h2>
                        <div className="mob-search__chips">
                            {TRENDING.map((item) => (
                                <button
                                    key={item.label}
                                    type="button"
                                    className="mob-search__chip"
                                    onClick={() => pick(item.query)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </section>
                ) : null}

                <section className="mob-search__block">
                    <h2 className="mob-search__heading">{isEmpty ? "Recent" : "Results"}</h2>

                    {results.length === 0 ? (
                        <p className="mob-search__empty">No results for &ldquo;{trimmed}&rdquo;</p>
                    ) : (
                        <ul className="mob-search__list">
                            {results.map((item) => (
                                <li key={item}>
                                    <button type="button" className="mob-search__row" onClick={() => pick(item)}>
                                        <IoTimeOutline className="mob-search__row-icon" aria-hidden />
                                        <span className="mob-search__row-text">{item}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}
